/* eslint-disable react-hooks/exhaustive-deps */

// Libraries
import _ from 'lodash';
import React from 'react';

// Supermove
import {Form, MapType, Space} from '@supermove/components';
import {gql} from '@supermove/graphql';
import {useResponsive} from '@supermove/hooks';
import {ProjectModel} from '@supermove/models';

// App
import JobForm from '@shared/modules/Job/forms/JobForm';
import ProjectBlockKind from '@shared/modules/Project/enums/ProjectBlockKind';
import Line from 'modules/App/components/Line';
import EditJobDispatchBlock from 'modules/Project/V2/Edit/components/EditJobDispatchBlock';
import EditJobInformationBlock from 'modules/Project/V2/Edit/components/EditJobInformationBlock';
import EditJobNotesBlock from 'modules/Project/V2/Edit/components/EditJobNotesBlock';
import EditJobStopsBlock from 'modules/Project/V2/Edit/components/EditJobStopsBlock';
import EditJobVariablesBlock from 'modules/Project/V2/Edit/components/EditJobVariablesBlock';

const EditJobBlock = ({
  kind,
  form,
  field,
  jobsField,
  project,
  layoutKey,
  index,
  map,
  handleShowSalesCalendar,
}: {
  kind: string;
  form: Form;
  field: string;
  jobsField: string;
  project: ProjectModel;
  layoutKey: string;
  index: number;
  map: MapType | null;
  handleShowSalesCalendar?: () => void;
}) => {
  // Every field should use this customized setter when setting form values
  form.customSetFieldValue = (editField: any, value: any) => {
    form.setFieldValue(`${field}.isUpdated`, true);
    form.setFieldValue(editField, value);
  };

  const isDisabled = _.get(form.values, `${field}.isFinal`);
  const {isMoversInputVisible, isTrucksInputVisible} = JobForm.getDispatchFieldVisibility(
    _.get(form.values, field),
    {
      isEnabledHideHardcodedBillingInputs:
        project.organization.features.isEnabledHideHardcodedBillingInputs,
    },
  );

  switch (kind) {
    case ProjectBlockKind.Job.JOB_INFORMATION:
      return (
        <EditJobInformationBlock
          form={form}
          field={field}
          index={index}
          project={project}
          isDisabled={isDisabled}
          handleShowSalesCalendar={handleShowSalesCalendar}
        />
      );
    case ProjectBlockKind.Job.NOTES:
      return <EditJobNotesBlock form={form} field={field} index={index} isDisabled={isDisabled} />;
    case ProjectBlockKind.Job.STOPS:
      return (
        <EditJobStopsBlock
          form={form}
          field={field}
          jobsField={jobsField}
          index={index}
          project={project}
          isDisabled={isDisabled}
          map={map}
        />
      );
    case ProjectBlockKind.Job.DISPATCH:
      return isMoversInputVisible && isTrucksInputVisible ? (
        <EditJobDispatchBlock
          form={form}
          field={field}
          index={index}
          layoutKey={layoutKey}
          isDisabled={isDisabled}
          isTrucksInputVisible={isTrucksInputVisible}
          isMoversInputVisible={isMoversInputVisible}
        />
      ) : null;
    case ProjectBlockKind.VARIABLES:
      return (
        <EditJobVariablesBlock
          form={form}
          field={field}
          index={index}
          layoutKey={layoutKey}
          isDisabled={isDisabled}
        />
      );
    default:
      return null;
  }
};

const EditProjectJobBlocks = ({
  form,
  field,
  jobsField,
  project,
  contentSize,
  map,
  handleShowSalesCalendar,
}: {
  form: Form;
  field: string;
  jobsField: string;
  project: ProjectModel;
  contentSize: number;
  map: MapType | null;
  handleShowSalesCalendar?: () => void;
}) => {
  const responsive = useResponsive();
  const blocks = responsive.desktop
    ? ProjectBlockKind.EditJobBlocks
    : ProjectBlockKind.MobileEditJobBlocks;

  return (
    <React.Fragment>
      {blocks.map((projectBlockKind, index) => {
        return (
          <React.Fragment key={projectBlockKind}>
            {index > 0 &&
              (responsive.desktop ? <Space height={24} /> : <Line style={{height: 4}} />)}
            <EditJobBlock
              layoutKey={contentSize.toString()}
              kind={projectBlockKind}
              form={form}
              field={field}
              jobsField={jobsField}
              project={project}
              index={index}
              map={map}
              handleShowSalesCalendar={handleShowSalesCalendar}
            />
          </React.Fragment>
        );
      })}
      {responsive.desktop && <Space height={120} />}
    </React.Fragment>
  );
};

// --------------------------------------------------
// Data
// --------------------------------------------------
EditProjectJobBlocks.fragment = gql`
  ${EditJobInformationBlock.fragment}
  ${EditJobStopsBlock.fragment}
  fragment EditProjectJobBlocks on Project {
    id
    organization {
      id
      features {
        isEnabledHideHardcodedBillingInputs: isEnabled(feature: "HIDE_HARDCODED_BILLING_INPUTS")
      }
    }
    ...EditJobInformationBlock
    ...EditJobStopsBlock
  }
`;

export default EditProjectJobBlocks;
